@if (loading) {
  <div class="np-page">
    <div class="np-intro">
      <span class="np-title">Notifications</span>
      <span class="np-subtitle">Choose what we tell you and how.</span>
    </div>
    <div class="np-card">
      <span class="np-section">What to notify</span>
      @for (row of [1, 2, 3, 4, 5, 6]; track row) {
        <div class="np-skeleton-row">
          <span class="np-skeleton np-skeleton--icon"></span>
          <span class="np-skeleton-text">
            <span class="np-skeleton np-skeleton--title"></span>
            <span class="np-skeleton np-skeleton--body"></span>
          </span>
        </div>
      }
    </div>
  </div>
} @else if (loadError || !draft) {
  <div class="np-page">
    <div class="np-card np-error">
      <i class="bx bx-bell-off" aria-hidden="true"></i>
      <span class="np-error-title">Couldn't load your preferences</span>
      <span class="np-error-body">
        The notification service did not answer. Nothing has been changed.
      </span>
      <button type="button" class="np-primary" (click)="load()">Try again</button>
    </div>
  </div>
} @else {
  <div class="np-page">
    <div class="np-intro">
      <span class="np-title">Notifications</span>
      <span class="np-subtitle">Choose what we tell you and how.</span>
    </div>

    <!-- Master switch -->
    <div class="np-card">
      <div class="np-row">
        <div class="np-row-text">
          <span class="np-row-title">Allow notifications</span>
          <span class="np-row-desc">{{ masterSubtitle }}</span>
        </div>
        <label class="switch">
          <input
            type="checkbox"
            role="switch"
            aria-label="Allow notifications"
            [checked]="draft.enabled"
            [disabled]="saving"
            (change)="setMaster($any($event.target).checked)"
          />
          <span class="slider"></span>
        </label>
      </div>
    </div>

    <!-- Everything below the master switch dims as one group -->
    <div class="np-dimmable" [class.np-dimmed]="!draft.enabled">
      <!-- Channels -->
      <div class="np-card">
        <span class="np-section">Channels</span>
        <div class="np-row">
          <div class="np-row-text">
            <span class="np-row-title">Push</span>
            <span class="np-row-desc">Alerts on this device</span>
          </div>
          <label class="switch">
            <input
              type="checkbox"
              role="switch"
              aria-label="Push"
              [checked]="channelOn('push')"
              [disabled]="!draft.enabled || saving"
              (change)="setChannel('push', $any($event.target).checked)"
            />
            <span class="slider"></span>
          </label>
        </div>
        <div class="np-row">
          <div class="np-row-text">
            <span class="np-row-title">Email</span>
            <span class="np-row-desc">{{ emailSubtitle }}</span>
          </div>
          <label class="switch">
            <input
              type="checkbox"
              role="switch"
              aria-label="Email"
              [checked]="channelOn('email')"
              [disabled]="!draft.enabled || saving"
              (change)="setChannel('email', $any($event.target).checked)"
            />
            <span class="slider"></span>
          </label>
        </div>
        <div class="np-row">
          <div class="np-row-text">
            <span class="np-row-title">SMS</span>
            <span class="np-row-desc">{{ smsSubtitle }}</span>
            @if (phone) {
              <span class="np-row-note">Standard rates may apply</span>
            }
          </div>
          <label class="switch">
            <input
              type="checkbox"
              role="switch"
              aria-label="SMS"
              [checked]="channelOn('sms')"
              [disabled]="!draft.enabled || saving"
              (change)="setChannel('sms', $any($event.target).checked)"
            />
            <span class="slider"></span>
          </label>
        </div>
      </div>

      <!-- What to notify -->
      <div class="np-card">
        <span class="np-section">What to notify</span>
        @if (draft.categories.length === 0) {
          <span class="np-row-desc">No categories are available for your role.</span>
        } @else {
          <div class="np-matrix-head">
            <span class="np-matrix-caption">CATEGORY</span>
            @for (channel of channels; track channel) {
              <span class="np-matrix-col" [class.np-col-off]="!channelOn(channel)">
                {{ channelLabels[channel] }}
              </span>
            }
          </div>
          @for (category of draft.categories; track category.key) {
            <div class="np-matrix-row">
              <span class="np-cat-icon">
                <i class="bx" [ngClass]="categoryIconFor(category)" aria-hidden="true"></i>
              </span>
              <span class="np-row-text np-cat-text">
                <span class="np-row-title">{{ category.label }}</span>
                <span class="np-row-desc">{{ category.description }}</span>
              </span>
              @for (channel of channels; track channel) {
                <span class="np-cell">
                  @if (!isAllowed(category, channel)) {
                    <span
                      class="np-dash"
                      [attr.aria-label]="
                        channelLabels[channel] + ' is not available for ' + category.label
                      "
                      >—</span
                    >
                  } @else if (isLocked(category, channel)) {
                    <i
                      class="bx bxs-lock-alt np-lock"
                      [matTooltip]="lockedMessage"
                      matTooltipPosition="above"
                      tabindex="0"
                      [attr.aria-label]="
                        category.label + ', ' + channelLabels[channel] + '. ' + lockedMessage
                      "
                    ></i>
                  } @else {
                    <label class="switch">
                      <input
                        type="checkbox"
                        role="switch"
                        [attr.aria-label]="category.label + ' ' + channelLabels[channel]"
                        [checked]="cellValue(category, channel)"
                        [disabled]="!cellEditable(category, channel)"
                        (change)="setCell(category, channel, $any($event.target).checked)"
                      />
                      <span class="slider"></span>
                    </label>
                  }
                </span>
              }
            </div>
          }
        }
      </div>

      <!-- Quiet hours -->
      <div class="np-card">
        <span class="np-section">Quiet hours</span>
        <div class="np-row">
          <div class="np-row-text">
            <span class="np-row-title">Quiet hours</span>
            <span class="np-row-desc">
              Push and SMS pause. Billing and order alerts still come through.
            </span>
          </div>
          <label class="switch">
            <input
              type="checkbox"
              role="switch"
              aria-label="Quiet hours"
              [checked]="draft.quiet_hours.enabled"
              [disabled]="!draft.enabled || saving"
              (change)="draft.quiet_hours.enabled = $any($event.target).checked"
            />
            <span class="slider"></span>
          </label>
        </div>
        <div class="np-fields">
          <label class="np-field">
            <span class="np-field-label">Start</span>
            <input
              type="time"
              [value]="draft.quiet_hours.start"
              [disabled]="!draft.enabled || !draft.quiet_hours.enabled || saving"
              (change)="draft.quiet_hours.start = $any($event.target).value"
            />
          </label>
          <label class="np-field">
            <span class="np-field-label">End</span>
            <input
              type="time"
              [value]="draft.quiet_hours.end"
              [disabled]="!draft.enabled || !draft.quiet_hours.enabled || saving"
              (change)="draft.quiet_hours.end = $any($event.target).value"
            />
          </label>
          <label class="np-field">
            <span class="np-field-label">Timezone</span>
            <select
              [value]="draft.quiet_hours.timezone"
              [disabled]="!draft.enabled || !draft.quiet_hours.enabled || saving"
              (change)="draft.quiet_hours.timezone = $any($event.target).value"
            >
              @for (zone of timezones; track zone.id) {
                <option [value]="zone.id">{{ zone.label }}</option>
              }
              @if (!timezones.length || !draft.quiet_hours.timezone) {
                <option [value]="draft.quiet_hours.timezone">
                  {{ draft.quiet_hours.timezone }}
                </option>
              }
            </select>
          </label>
        </div>
      </div>

      <!-- Summary schedule -->
      <div class="np-card">
        <span class="np-section">Summary schedule</span>
        <div class="np-segments" role="radiogroup" aria-label="Summary cadence">
          @for (option of cadences; track option.value) {
            <button
              type="button"
              role="radio"
              class="np-segment"
              [class.np-segment--on]="draft.summary.cadence === option.value"
              [attr.aria-checked]="draft.summary.cadence === option.value"
              [disabled]="!draft.enabled || saving"
              (click)="setCadence(option.value)"
            >
              {{ option.label }}
            </button>
          }
        </div>
        <div class="np-row">
          <div class="np-row-text">
            <span class="np-row-title">Send at</span>
            <span class="np-row-desc">Applies to Sales &amp; Reports</span>
          </div>
          <input
            class="np-time"
            type="time"
            aria-label="Send summary at"
            [value]="draft.summary.time"
            [disabled]="!draft.enabled || draft.summary.cadence === 'off' || saving"
            (change)="draft.summary.time = $any($event.target).value"
          />
        </div>
      </div>

      <!-- Advanced -->
      <div class="np-card">
        <button
          type="button"
          class="np-disclosure"
          [attr.aria-expanded]="advancedOpen"
          (click)="advancedOpen = !advancedOpen"
        >
          <span class="np-row-text">
            <span class="np-section np-section--flush">Advanced</span>
            <span class="np-row-desc">{{ advancedSummary }}</span>
          </span>
          <i class="bx" [ngClass]="advancedOpen ? 'bx-chevron-up' : 'bx-chevron-down'"></i>
        </button>
        @if (advancedOpen) {
          <div class="np-row">
            <div class="np-row-text">
              <span class="np-row-title">Low rating alert</span>
              <span class="np-row-desc">Tell me about reviews at or below this rating.</span>
            </div>
            <div class="np-stepper">
              <button
                type="button"
                aria-label="Lower the rating threshold"
                [disabled]="!draft.enabled || saving || draft.thresholds.low_rating <= 1"
                (click)="setLowRating(-1)"
              >
                <i class="bx bx-minus" aria-hidden="true"></i>
              </button>
              <span class="np-stepper-value">{{ draft.thresholds.low_rating }}★</span>
              <button
                type="button"
                aria-label="Raise the rating threshold"
                [disabled]="!draft.enabled || saving || draft.thresholds.low_rating >= 5"
                (click)="setLowRating(1)"
              >
                <i class="bx bx-plus" aria-hidden="true"></i>
              </button>
            </div>
          </div>
          <div class="np-row">
            <div class="np-row-text">
              <span class="np-row-title">Large order amount</span>
              <span class="np-row-desc"
                >Flag orders at or above this total. Empty turns it off.</span
              >
            </div>
            <div class="np-amount">
              <span class="np-amount-prefix">$</span>
              <input
                type="number"
                min="0"
                step="1"
                inputmode="decimal"
                aria-label="Large order amount"
                placeholder="Off"
                [value]="draft.thresholds.large_order_amount ?? ''"
                [disabled]="!draft.enabled || saving"
                (change)="setLargeOrderAmount($any($event.target).value)"
              />
              @if (draft.thresholds.large_order_amount != null) {
                <button
                  type="button"
                  class="np-amount-clear"
                  aria-label="Turn the large order alert off"
                  [disabled]="!draft.enabled || saving"
                  (click)="clearLargeOrderAmount()"
                >
                  <i class="bx bx-x" aria-hidden="true"></i>
                </button>
              }
            </div>
          </div>
        }
      </div>

      <!-- Muted restaurants -->
      @if (mutedRestaurants.length > 0) {
        <div class="np-card">
          <span class="np-section">Muted restaurants</span>
          @for (restaurant of mutedRestaurants; track restaurant.id) {
            <div class="np-row">
              <div class="np-row-text">
                <span class="np-row-title">{{ restaurant.name }}</span>
                <span class="np-row-desc">
                  {{
                    restaurant.isCurrent
                      ? 'Current restaurant'
                      : 'Mute notifications from this location'
                  }}
                </span>
              </div>
              <label class="switch">
                <input
                  type="checkbox"
                  role="switch"
                  [attr.aria-label]="restaurant.name"
                  [checked]="restaurantNotifying(restaurant.id)"
                  [disabled]="!draft.enabled || saving"
                  (change)="setRestaurantMuted(restaurant.id, !$any($event.target).checked)"
                />
                <span class="slider"></span>
              </label>
            </div>
          }
        </div>
      }
    </div>

    <footer class="np-foot">
      <span class="np-hint">
        @if (dirty) {
          Unsaved changes
        } @else {
          All changes saved
        }
      </span>
      <a class="np-link" routerLink="/main/notifications">
        <i class="bx bx-bell" aria-hidden="true"></i> Open notifications
      </a>
      <button type="button" class="np-ghost" [disabled]="!canSave" (click)="cancel()">
        Cancel
      </button>
      <button type="button" class="np-primary" [disabled]="!canSave" (click)="save()">
        {{ saving ? 'Saving…' : 'Save changes' }}
      </button>
    </footer>
  </div>
}
